import { differenceInYears, isValid, parse } from "date-fns";

const parseBirthDateFromSSN = (ssn: string | undefined): Date | undefined => {
  if (!ssn) return undefined;

  const match = /^(\d{2})(\d{2})(\d{2})([A+-])[0-9a-zA-Z]+$/.exec(ssn);
  if (!match) {
    return undefined;
  }
  const [, day, month, year, centurySign] = match;

  const centuryMap: Record<string, string> = {
    "+": "18",
    "-": "19",
    A: "20",
  };
  const century = centuryMap[centurySign];
  if (!century) return undefined;

  const birthDateStr = `${century}${year}${month}${day}`;
  const birthDate = parse(birthDateStr, "yyyyMMdd", new Date());

  return isValid(birthDate) ? birthDate : undefined;
};

const calculateAge = (ssn: string | undefined) => {
  const birthDate = parseBirthDateFromSSN(ssn);
  if (birthDate === undefined) {
    return undefined;
  }
  return differenceInYears(new Date(), birthDate);
};

const isAtLeast = (
  ssn: string | undefined,
  age: number,
  lessThenOrEqual: boolean = true,
): boolean => isAge(ssn, age, "atLeast", lessThenOrEqual);

const isAtMost = (
  ssn: string | undefined,
  age: number,
  greaterThenOrEqual: boolean = true,
): boolean => isAge(ssn, age, "atMost", greaterThenOrEqual);

type AgeComparison = "atLeast" | "atMost";

const isAge = (
  ssn: string | undefined,
  age: number,
  comparison: AgeComparison,
  inclusive: boolean = true,
): boolean => {
  const foundAge = calculateAge(ssn);
  if (foundAge === undefined) return false;

  if (comparison === "atLeast") {
    return inclusive ? foundAge >= age : foundAge > age;
  }
  return inclusive ? foundAge <= age : foundAge < age;
};

export class AgeValidator {
  readonly ssn?: string;
  readonly age?: number;
  readonly isAtLeast18: boolean;
  readonly isAtLeast80: boolean;
  readonly isAtMost45: boolean;
  readonly birthDate?: Date;

  constructor(ssn?: string) {
    this.ssn = ssn;
    this.age = calculateAge(ssn);
    this.isAtLeast18 = isAtLeast(ssn, 18);
    this.isAtLeast80 = isAtLeast(ssn, 80);
    this.isAtMost45 = isAtMost(ssn, 45);
    this.birthDate = parseBirthDateFromSSN(ssn);
  }

  isAtLeast = (age: number) => isAtLeast(this.ssn, age);
  isAtMost = (age: number) => isAtMost(this.ssn, age);
}
